import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, map, share, takeUntil, tap } from 'rxjs/operators';
import { BEFH_ERROR_MARKUP, CARD_ERROR_MARKUP, LOAN_ERROR_MARKUP } from '../../config/accountErrors.config';
import { AccountsService } from 'src/app/feature-modules/accounts/services/accounts.service';
import { RouteWatcherService } from '../../feature-modules/shared/services/route-watcher.service';
import { NotificationService } from '../../services/notification.service';
import { CookieService } from 'ngx-cookie-service';
import { EventTrackingService } from '../../services/event-tracking.service';
import { CARD_CPID } from '../../config/account.config';
import { Offer } from '../../feature-modules/offers/models/offers.model';
import { NavError, NavItem, NavTypes } from './header-nav.model';
import { HeaderNavViewModel, filterFalsyViewModelProps, selectHeaderNavViewModelProps } from './header-nav.util';
import { CardAccount } from 'src/app/feature-modules/accounts/models/accounts.model';
import { CommonModule } from '@angular/common';
import { IconCloseComponent } from '../../feature-modules/shared/components/icon-close/icon-close.component';
import { IconExternalLinkComponent } from '../../feature-modules/shared/components/icon-external-link/icon-external-link.component';
import { IconHamburgerComponent } from '../../feature-modules/shared/components/icon-hamburger/icon-hamburger.component';
import { IconSignOutComponent } from '../../feature-modules/shared/components/icon-sign-out/icon-sign-out.component';
import { IconUserComponent } from '../../feature-modules/shared/components/icon-user/icon-user.component';
import { SharedModule } from '../../feature-modules/shared/shared.module';
import { BeLogoV2Component } from '../../feature-modules/shared/components/be-logo-v2/be-logo-v2.component';
import { IconHomeComponent } from '../../feature-modules/shared/components/icon-home/icon-home.component';
import { IconDollarCircleComponent } from '../../feature-modules/shared/components/icon-dollar-circle/icon-dollar-circle.component';
import { IconCreditCardComponent } from '../../feature-modules/shared/components/icon-credit-card/icon-credit-card.component';
import { IconCreditGaugeComponent } from '../../feature-modules/shared/components/icon-credit-guage/icon-credit-gauge.component';
import { IconOfferBagComponent } from '../../feature-modules/shared/components/icon-offer-bag/icon-offer-bag.component';
import { FilterOffersPipe } from './filter-offers.pipe';
import { AuthService } from '../../services/auth.service';
import { HubCardVerificationModalComponent } from '../../feature-modules/shared/components/hub-card-verification-modal/hub-card-verification-modal.component';
import { handleOverlappingElements } from '../../utils/dom.util';

const navErrorMapping = {
  loan: { type: 'loan', markup: LOAN_ERROR_MARKUP },
  card: { type: 'card', markup: CARD_ERROR_MARKUP },
  befh: { type: 'befh', markup: BEFH_ERROR_MARKUP },
};

@Component({
    selector: 'app-header-nav',
    templateUrl: './header-nav.component.html',
    styleUrls: ['./header-nav.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        RouterModule,
        BeLogoV2Component,
        IconUserComponent,
        IconCloseComponent,
        IconSignOutComponent,
        IconHamburgerComponent,
        IconExternalLinkComponent,
        IconHomeComponent,
        IconDollarCircleComponent,
        IconCreditCardComponent,
        IconCreditGaugeComponent,
        IconOfferBagComponent,
        FilterOffersPipe,
        SharedModule,
    ]
})
export class HeaderNavComponent implements OnInit, OnDestroy {
  @ViewChild('snackBarTemplate') template: TemplateRef<any>;

  cid: string;
  navItems: NavItem[] = [];
  desktopDropdownItems: NavItem[];
  subMenuDesktopHeight = '0';
  isNavExpanded = false;
  hasGatewayError: boolean;
  isErrorDrawerExpanded = false;
  navError: NavError;
  isRequiredActionDot: boolean;
  private currentUrl: string = '';

  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

  private viewModel$ = this.store.select(selectHeaderNavViewModelProps).pipe(
    takeUntil(this.ngUnsubscribe$),
    filterFalsyViewModelProps(),
    map((vmProps) => new HeaderNavViewModel(vmProps)),
    share()
  );
  viewModel: HeaderNavViewModel;

  isProfileActive$ = this.routeWatcherService.currentUrl$.pipe(
    map((url) => url.includes('/profile')),
    tap((isProfileActive) => {
      isProfileActive && this.resetPrimaryNavItemsActiveState();
    })
  );

  isOffersActive$ = this.routeWatcherService.currentUrl$.pipe(
    map((url) => url.includes('/offers')),
    distinctUntilChanged()
  );

  isRequiredActionDot$ = combineLatest([this.isProfileActive$, this.viewModel$]).pipe(
    map(([isProfileActive, viewModel]) => !isProfileActive && viewModel.incomeRefreshRequired)
  );

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly authService: AuthService,
    private readonly notificationService: NotificationService,
    private readonly routeWatcherService: RouteWatcherService,
    private readonly cookieService: CookieService,
    private readonly eventTrackingService: EventTrackingService,
    private readonly accountsService: AccountsService
  ) {}

  ngOnInit(): void {
    this.cid = this.cookieService.get('cid');

    this.viewModel$.subscribe((vm) => {
      this.viewModel = vm;
      this.navItems = vm.getNavItems();
      this.hasGatewayError = !!vm.gatewayError;
      // Update active nav when navItems are loaded
      this.updateActiveNavFromRoute();
    });

    this.isRequiredActionDot$.subscribe((showDot) => {
      this.isRequiredActionDot = showDot;
    });

    // Listen to route changes and update active nav
    this.routeWatcherService.currentUrl$.pipe(
      takeUntil(this.ngUnsubscribe$)
    ).subscribe((url) => {
      this.currentUrl = url;
      this.updateActiveNavFromRoute();
    });
  }

  private updateActiveNavFromRoute(): void {
    if (!this.navItems || this.navItems.length === 0 || !this.currentUrl) {
      return;
    }

    if (this.currentUrl.includes('/profile')) {
      // Profile resets all nav items
      this.resetPrimaryNavItemsActiveState();
      return;
    }

    let activeNavType: NavTypes;
    if (this.currentUrl.includes('/offers')) {
      activeNavType = 'offers';
    } else {
      // Home is the default
      activeNavType = 'home';
    }

    // Update navItems active state
    for (let navItem of this.navItems) {
      navItem.isActive = navItem.type === activeNavType;
    }
  }

  calculatePortalLinkType(offer: Offer) {
    let customerPortalClickLinkType: string;
    if (offer.productType === 'CREDIT_CARD') {
      customerPortalClickLinkType =
        offer.offerType === 'INVITATION_TO_APPLY' ? 'Credit Card ITA' : 'Credit Card Express Lane';
    } else {
      customerPortalClickLinkType = offer.offerType === 'INVITATION_TO_APPLY' ? 'Loan ITA' : 'Loan Express Lane';
    }
    return customerPortalClickLinkType;
  }

  setActiveNavByType(activeNavType: NavTypes) {
    if (this.navItems && this.navItems.length > 0) {
      for (let navItem of this.navItems) {
        navItem.isActive = navItem.type === activeNavType;
      }
    }
  }

  ngOnDestroy() {
    // Ensure body scroll is re-enabled if component is destroyed while nav is open
    this.setBodyScroll(false);
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  onNavClick(navItem: NavItem) {
    if (navItem.type === 'home') {
      return this.onHomeClick(navItem);
    } else if (navItem.type === 'loan') {
      return this.onLoanClick(navItem);
    } else if (navItem.type === 'card') {
      return this.onCardClick(navItem);
    } else if (navItem.type === 'befh') {
      return this.onBefhClick(navItem);
    } else if (navItem.type === 'offers') {
      return this.onOffersClick();
    }
  }

  resetNonLoanDropdowns() {
    // if credit nav item exists, we can expect indexes to be as follows
    if (this.navItems.length === 4) {
      // reset expanded errors if present
      this.navItems[3].isErrorExpanded = false;
      this.navItems[2].isErrorExpanded = false;
      // reset other submenu
      this.navItems[2].isSubMenuExpanded = false;
      this.navItems[2].subItemsMobileHeight = '0';
    } else if (this.navItems.length === 3) {
      // if no credit nav item
      this.navItems[2].isErrorExpanded = false;
    }
  }

  resetNonCardDropdowns() {
    // if loan nav item exists, we can expect indexes to be as follows
    if (this.navItems.length === 4) {
      // reset expanded errors if present
      this.navItems[3].isErrorExpanded = false;
      this.navItems[1].isErrorExpanded = false;
      // reset other submenu
      this.navItems[1].isSubMenuExpanded = false;
      this.navItems[1].subItemsMobileHeight = '0';
    } else if (this.navItems.length === 3) {
      // if no loan nav item
      this.navItems[2].isErrorExpanded = false;
    }
  }

  resetNonBefhDropdowns() {
    const loanNavItem = this.navItems.find(({ type }) => type === 'loan');
    if (loanNavItem) {
      // reset expanded errors if present
      loanNavItem.isErrorExpanded = false;
      // reset other submenu
      loanNavItem.isSubMenuExpanded = false;
      loanNavItem.subItemsMobileHeight = '0';
    }
    const cardNavItem = this.navItems.find(({ type }) => type === 'card');
    if (cardNavItem) {
      // reset expanded errors if present
      cardNavItem.isErrorExpanded = false;
      // reset other submenu
      cardNavItem.isSubMenuExpanded = false;
      cardNavItem.subItemsMobileHeight = '0';
    }
  }

  resetDesktopDropdown() {
    this.desktopDropdownItems = [];
    this.subMenuDesktopHeight = '0';
  }

  getNavErrorMapping(navType: string) {
    return navErrorMapping[navType] || null;
  }

  setNavError(navItem: NavItem) {
    this.navError = this.getNavErrorMapping(navItem.type);
    this.isErrorDrawerExpanded = true;
    navItem.isErrorExpanded = true;
  }

  closeErrorDrawer(navItem: NavItem) {
    this.isErrorDrawerExpanded = false;
    navItem.isErrorExpanded = false;
  }

  onHomeClick(navItem: NavItem) {
    this.router.navigateByUrl('customer-hub');
    this.closeNavSlide(true);
    navItem.isActive = true;
  }

  onOffersClick() {
    this.router.navigateByUrl('offers');
    this.closeNavSlide(true);
  }

  onLoanClick(navItem: NavItem) {
    // launch snackbar if 400/500 err
    if (this.hasGatewayError) {
      this.notificationService.displayCustomErrorSnackbar(LOAN_ERROR_MARKUP);
      return;
    }
    if (navItem.isDisabled) {
      if (this.isErrorDrawerExpanded && this.navError.type === navItem.type) {
        this.closeErrorDrawer(navItem);
        return;
      }
      // error open logic
      this.setNavError(navItem);
      this.resetNonLoanDropdowns();
      this.resetDesktopDropdown();
      return;
    }
    // submenu toggle logic
    if (navItem?.subItems?.length) {
      this.toggleSubmenu(navItem);
      this.isErrorDrawerExpanded = false;
      this.resetNonLoanDropdowns();
      return;
    }

    this.accountsService.navigateToPersonalLoan({
      url: navItem.url,
      productType: navItem.productType,
      accountStatus: navItem.accountStatus,
      last4DigitsExternalAccountNumber: navItem.last4DigitsExternalAccountNumber,
      id: navItem.id,
      repossessionCaseStatus: navItem.repossessionCaseStatus,
      reinstatedDate: navItem.reinstatedDate,
    });
  }

  onCardClick(navItem: NavItem) {
    // launch snackbar if 400/500 err
    if (this.hasGatewayError) {
      this.notificationService.displayCustomErrorSnackbar(CARD_ERROR_MARKUP);
      return;
    }
    if (navItem.isDisabled) {
      // close error drawer if open
      if (this.isErrorDrawerExpanded && this.navError.type === navItem.type) {
        this.closeErrorDrawer(navItem);
        return;
      }
      // error open logic
      this.setNavError(navItem);
      this.resetNonCardDropdowns();
      this.resetDesktopDropdown();
      return;
    }

    // submenu toggle logic
    if (navItem?.subItems?.length) {
      this.toggleSubmenu(navItem);
      this.isErrorDrawerExpanded = false;
      this.resetNonCardDropdowns();
      return;
    }

    if (navItem?.url?.includes(CARD_CPID)) {
      this.eventTrackingService.trackEnterpriseEvent({
        authClickLinkType: 'clickNavigationForCardApplication',
        cid: this.cid,
        eventName: 'linkClick',
        authClickLinkText: navItem.title,
        cpid: CARD_CPID,
      });
    }
    this.accountsService.navigateToCreditCard(navItem as CardAccount, HubCardVerificationModalComponent, navItem.url);
  }

  onBefhClick(navItem: NavItem) {
    // launch snackbar if 400/500 err
    if (this.hasGatewayError) {
      this.notificationService.displayCustomErrorSnackbar(BEFH_ERROR_MARKUP);
      return;
    }
    if (navItem.isDisabled) {
      // close error drawer if open
      if (this.isErrorDrawerExpanded && this.navError.type === navItem.type) {
        this.closeErrorDrawer(navItem);
        return;
      }
      // error open logic
      this.setNavError(navItem);
      this.resetNonBefhDropdowns();
      this.resetDesktopDropdown();
      return;
    }
    this.accountsService.navigateToCreditHealth(navItem.url, navItem.kbaCompleted);
  }

  toggleSubmenu(navItem: NavItem) {
    navItem.subItemsMobileHeight = navItem.isSubMenuExpanded ? '0' : this.getSubmenuMobileHeight(navItem);
    this.subMenuDesktopHeight = navItem.isSubMenuExpanded ? '0' : '64px';
    navItem.isSubMenuExpanded = !navItem.isSubMenuExpanded;
    this.desktopDropdownItems = navItem.subItems;
  }

  getSubmenuMobileHeight(navItem: NavItem) {
    return `${navItem.subItems.length * 48.8}px`;
  }

  toggleNav() {
    this.isNavExpanded = !this.isNavExpanded;
    handleOverlappingElements(this.isNavExpanded);
    this.setBodyScroll(this.isNavExpanded);
  }

  logOut() {
    this.authService.logOut();
  }

  resetPrimaryNavItemsActiveState() {
    this.navItems.forEach((nav) => (nav.isActive = false));
  }

  closeNavSlide(isDelay: boolean) {
    // delay for aesthetic purposes (to be used for in app transitions)

    if (isDelay) {
      setTimeout(() => {
        this.isNavExpanded = false;
      }, 100);
    } else {
      this.isNavExpanded = false;
    }
    this.setBodyScroll(false);
    handleOverlappingElements(false);
  }

  getOffersCount(): number {
    const offersNavItem = this.navItems.find(item => item.type === 'offers');
    return offersNavItem?.offerCount || 0;
  }

  private setBodyScroll(disable: boolean): void {
    if (disable) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }
}
